import { ReactElement } from "react";
import { FieldErrors, FieldValues } from "react-hook-form";

type ErrorListProps<FormType extends FieldValues> = {
  errors: FieldErrors<FormType>;
  serverErrors?: Record<string, string>;
};

const ErrorList = <FormType extends FieldValues>({
  errors,
  serverErrors,
}: ErrorListProps<FormType>): null | ReactElement => {
  const hasFormErrors = errors && Object.keys(errors).length > 0;
  const hasServerErrors = serverErrors && Object.keys(serverErrors).length > 0;

  if (!hasFormErrors && !hasServerErrors) return null;

  return (
    <div className="mb-6 space-y-4">
      {hasServerErrors && (
        <div className="rounded-md border border-red-600 bg-red-950/50 p-4">
          <div className="text-sm font-medium text-red-300">Server Error</div>
          <div className="mt-2 text-sm text-red-200">
            <ul className="list-inside list-disc space-y-1">
              {Object.entries(serverErrors).map(([field, message]) => (
                <li key={field}>
                  {field !== "general" && <strong>{field}:</strong>} {message}
                </li>
              ))}
            </ul>
          </div>
        </div>
      )}

      {hasFormErrors && (
        <div className="rounded-md border border-orange-600 bg-orange-950/50 p-4">
          <div className="text-sm font-medium text-orange-300">
            Validation Errors
          </div>
          <div className="mt-2 text-sm text-orange-200">
            <ul className="list-inside list-disc space-y-1">
              {Object.entries(errors).map(([field, error]) => {
                if (Array.isArray(error)) {
                  // Handle array errors (e.g., entityMappings)
                  return error.map((item, index) =>
                    item
                      ? Object.entries(item).map(
                          ([nestedField, nestedError]) => (
                            <li key={`${field}-${index}-${nestedField}`}>
                              <strong>
                                [{field} {index + 1}] {nestedField}:
                              </strong>{" "}
                              {nestedError &&
                              typeof nestedError === "object" &&
                              "message" in nestedError
                                ? String(nestedError.message)
                                : "Invalid error"}
                            </li>
                          ),
                        )
                      : null,
                  );
                } else {
                  return (
                    <li key={field}>
                      <strong>{field}:</strong>{" "}
                      {error && typeof error === "object" && "message" in error
                        ? String(error.message)
                        : "Invalid error"}
                    </li>
                  );
                }
              })}
            </ul>
          </div>
        </div>
      )}
    </div>
  );
};

export default ErrorList;
